/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Button } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { ItemStatCd, MpItem, MpItemAtchFileTpCd, MpItemCondition } from 'models/mp/MpItem';
import { addBookmarkItem, findMpItems } from 'apis/mp/MpItem';
import FileUploadPopUp from '../common/components/FileUploadPopUp';
import { useMessageBar } from 'hooks/useMessageBar';
import { BizName, ManagementMode } from 'models/common/Common';
import { getExcelFileName } from 'utils/ExcelUtil';
import MPCommonSearch from './base/MPCommonSearch';
import { hasRole } from 'utils/SessionUtil';
import { CellMaker } from '@grapecity/wijmo.grid.cellmaker';
import CustomGrid from 'components/grids/CustomGrid';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import { BmkAtcTpCd, BmkTgtTpCd, BookmarkMaster } from 'models/common/Bookmark';
import AutoSendEmailModal from 'components/modals/common/AutoSendEmailModal';
import { CommonUtil } from 'utils/CommonUtil';
import useSessionStore from 'stores/useSessionStore';
import MpItemStatHisModal from '../../components/modals/mp/MpItemStatHisModal';
import MpMcrcPopUp from './popup/MpMcrcPopUp';
import { MpDrwRetrievePopUp } from './popup/MpDrwRetrievePopUp';
import { open4mcc } from '../../utils/ApiUtil';
import { MpEquipmentPopUp } from './popup/MpEquipmentPopUp';
import { saveCondBookmarks } from 'apis/common/Bookmark';
import GridSettingButtons from 'components/grids/GridSettingButtons';

/**
 * @description 설비변경점 등록 및 조회
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 *  25.10.24    jihwan89       설비변경점 개선사항 v5.2 31행 - 관심항목 조회 및 등록 추가
 *                                                         Grid에서 버튼눌러 추가/삭제 및 그리드 상단 관심항목버튼으로 조회
 */

const MpItemPage = () => {
  //상태변경이력팝업
  const [isMpItemStatHisModalOpen, setMpItemStatHisModalOpen] = useState<boolean>(false);
  const [mpItemStatHisModalCondition, setMpItemStatHisModalCondition] = useState<any>({
    mpItemId: '',
  });

  const { langCd } = useSessionStore();
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const navigate = useNavigate();
  const [mpItemCondition, setMpItemCondition] = useState<MpItemCondition>({
    startDataInsDtm: dayjs().add(-2, 'year').format('YYYY.MM.DD'),
    endDataInsDtm: dayjs().format('YYYY.MM.DD'),
    mpClsfCd: useSessionStore.getState().mpClsfType || '',
  });
  const gridRef = useRef<any>();
  const gridRef2 = useRef<any>();
  const [isChangeColumn, setIsChangeColumn] = useState<boolean>(false);

  const [rowData, setRowData] = useState<MpItem[]>([]);
  const [checkedItems, setCheckedItems] = useState<MpItem[]>([]);
  const [isOpenMpItemEqpListModal, setOpenMpItemEqpListModal] = useState<boolean>(false);
  const [mpItemEqpListModalCondition, setMpItemEqpListModalCondition] = useState<any>({});
  const [isOpenFileUploadModal, setOpenFileUploadModal] = useState<boolean>(false);
  const [fileUploadModalCondition, setFileUploadModalCondition] = useState<any>({});
  const [isOpenMcrcPopup, setOpenMcrcPopup] = useState<boolean>(false);
  const [mcrcPopupCondition, setMcrcPopupCondition] = useState<any>({});
  const [isOpenDrwRetrievePopUp, setOpenDrwRetrievePopUp] = useState<boolean>(false);
  const [drwRetrievePopUpCondition, setDrwRetrievePopUpCondition] = useState<any>({});
  const [isOpenEmailModal, setOpenEmailModal] = useState<boolean>(false);
  const [hasAuth, setHasAuth] = useState<boolean>(false);
  const [bmkGrId, setBmkGrId] = useState<string>();
  const [flexRef, setflexRef] = useState<any>();

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'bmkYn',
        header: String(t('mp.grid.관심항목', '관심항목')),
        width: 60,
        align: 'center',
        cssClass: 'WijmoBookmark',
        cellTemplate: (ctx) =>
          `<button class="${ctx.value === 'Y' ? 'detail' : ''}" id="bmkBtn"></button>`,
      },
      {
        header: String(t('mp.grid.Item', 'Item')),
        columns: [
          {
            binding: 'itemId',
            header: String(t('mp.grid.번호', '번호')),
            width: 105,
            align: 'center',
            cellTemplate: CellMaker.makeLink({
              text: '${item.itemId}',
              click: (e, ctx) => {
                if (ctx && Object.keys(ctx).includes('value')) {
                  moveToModify(ctx['value']);
                }
              },
            }),
          },
          {
            binding: 'itemEngNm',
            header: String(t('mp.grid.표준명', '표준명')),
            width: 200,
            cellTemplate: (params) => {
              return CommonUtil.safeHtmlContents(params.value);
            },
          },
          {
            binding: 'itemLocalNm',
            header: String(t('mp.grid.Local 명', 'Local 명')),
            width: 200,
            cellTemplate: (params) => {
              return CommonUtil.safeHtmlContents(
                (langCd || '').startsWith('zh')
                  ? params.item?.itemCngNm || ''
                  : langCd === 'ko'
                  ? params.item?.itemNm || ''
                  : params.item?.itemEngNm || ''
              );
            },
          },
        ],
      },
      {
        binding: 'mpClsfCdNm',
        header: String(t('mp.grid.분류', '분류')),
        align: 'center',
        width: 106,
      },
      {
        binding: 'mpItemTpCdNm',
        header: String(t('mp.grid.진행단계', '진행단계')),
        width: 100,
      },
      {
        binding: 'itemStatCdNm',
        header: String(t('mp.grid.상태', '상태')),
        width: 100,
        cssClass: 'WijmoPopup',
        cellTemplate: (params) => `
          ${params.item.itemStatCdNm || ''}
          ${params.item.itemStatCdNm ? '<Button />' : ''}
        `,
      },
      {
        header: String(t('mp.grid.개선목적', '개선목적')),
        align: 'center',
        columns: [
          {
            binding: 'iprvClsfLv1CdNm',
            header: String(t('mp.grid.Level 1', 'Level 1')),
            width: 130,
          },
          {
            binding: 'iprvClsfLv2CdNm',
            header: String(t('mp.grid.Level 2', 'Level 2')),
            width: 130,
          },
          {
            binding: 'iprvClsfLv3CdNm',
            header: String(t('mp.grid.Level 3', 'Level 3')),
            width: 130,
          },
        ],
      },
      {
        binding: 'ecoYn',
        header: String(t('mp.grid.NC/EC', 'NC/EC')),
        width: 95,
        align: 'center',
        cellTemplate: (params) => `
          ${params.value ? (params.value === 'Y' ? 'EC' : 'NC') : ''}
        `,
      },
      {
        binding: 'tgtCopCd',
        header: String(t('mp.grid.법인', '법인')),
        width: 97,
      },
      {
        binding: 'procCdNm',
        header: String(t('mp.grid.공정', '공정')),
        align: 'center',
        width: 110,
      },
      {
        binding: 'eqpGr',
        header: String(t('com.label.설비군', '설비군')),
        width: 150,
      },
      {
        binding: 'tgtPlntCdNm',
        header: String(t('mp.grid.대상 Factory', '대상 Factory')),
        width: 100,
      },
      {
        binding: 'eqpNms',
        header: String(t('mp.grid.대상설비', '대상설비')),
        width: 200,
        cssClass: 'WijmoFind',
        cellTemplate: (ctx) => `
          <span>${ctx.value || ''}</span>
          ${ctx.value ? '<Button />' : ''}`,
      },
      {
        binding: 'reqMgrNm',
        header: String(t('mp.grid.담당자', '담당자')),
        width: 110,
        align: 'center',
      },
      {
        binding: 'heYn',
        header: String(t('mp.label.수평전개', '수평전개')),
        width: 100,
        align: 'center',
        cellTemplate: (params) => {
          if (!params.value) return '';
          return params.value === 'Y'
            ? String(t('mp.label.진행', '진행'))
            : String(t('mp.label.보류', '보류'));
        },
      },
      {
        binding: 'fstrYn',
        header: String(t('mp.grid.Fast Track 여부', 'Fast Track 여부')),
        width: 140,
        align: 'center',
      },
      {
        binding: 'rawSysNm',
        header: String(t('mp.grid.원천시스템', '원천시스템')),
        width: 117,
        align: 'center',
      },
      {
        header: String(t('mp.grid.4MCC', '4MCC')),
        columns: [
          {
            binding: 'm4ChgNo',
            header: String(t('mp.grid.발행번호', '발행번호')),
            width: 127,
            align: 'center',
            cellTemplate: CellMaker.makeLink({
              click: (e, ctx) => open4mcc(ctx.value),
            }),
          },
          {
            binding: 'm4StatNm',
            header: String(t('mp.grid.상태', '상태')),
            width: 100,
          },
          {
            binding: 'm4LvNm',
            header: String(t('mp.grid.Level', 'Level')),
            width: 100,
            cellTemplate: (params) =>
              `${
                params.item.ecoYn === 'Y' && params.value && params.value !== 'null'
                  ? params.value
                  : ''
              }`,
          },
        ],
      },
      {
        binding: 'oldRevCtn',
        header: String(t('mp.grid.변경전 내용', '변경전 내용')),
        width: 114,
      },
      {
        binding: 'newRevCtn',
        header: String(t('mp.grid.변경후 내용', '변경후 내용')),
        width: 114,
      },
      {
        header: String(t('mp.grid.첨부', '첨부')),
        columns: [
          {
            binding: 'atchFileCnt',
            header: String(t('mp.grid.일반첨부', '일반첨부')),
            width: 91,
            align: 'center',
            cellTemplate: (params) => `
              <div class="fileDiv">
                <span><em>${(params.value || 0).toLocaleString()}</em></span>
              </div>
            `,
          },
          {
            binding: 'mpItemRptAtchFileCnt',
            header: String(t('mp.grid.개조/개선 Item 보고서', '개조/개선 Item 보고서')),
            width: 121,
            align: 'center',
            cellTemplate: (params) => `
              <div class="fileDiv">
                <span><em>${(params.value || 0).toLocaleString()}</em></span>
              </div>
            `,
          },
          {
            binding: 'vaidEvlRptAtchFileCnt',
            header: String(t('mp.grid.유효성 평가보고서', '유효성 평가보고서')),
            width: 144,
            align: 'center',
            cellTemplate: (params) => `
              <div class="fileDiv">
                <span><em>${(params.value || 0).toLocaleString()}</em></span>
              </div>
            `,
          },
          {
            binding: 'scmmMintAtchFileCnt',
            header: String(t('mp.grid.회의록(설비변경 심의)', '회의록(설비변경 심의)')),
            width: 126,
            align: 'center',
            cellTemplate: (params) => `
              <div class="fileDiv">
                <span><em>${(params.value || 0).toLocaleString()}</em></span>
              </div>
            `,
          },
          {
            binding: 'mpFstrAtchFileCnt',
            header: String(t('mp.grid.Fast Track 근거자료', 'Fast Track 근거자료')),
            width: 120,
            align: 'center',
            cellTemplate: (params) => `
              <div class="fileDiv">
                <span><em>${(params.value || 0).toLocaleString()}</em></span>
              </div>
            `,
          },
          {
            binding: 'mpHeRptAtchFileCnt',
            header: String(t('mp.grid.회의록(수평전개 심의)', '회의록(수평전개 심의)')),
            width: 134,
            align: 'center',
            cellTemplate: (params) => `
              <div class="fileDiv">
                <span><em>${(params.value || 0).toLocaleString()}</em></span>
              </div>
            `,
          },
        ],
      },
      {
        binding: 'mcrcNms',
        header: String(t('mp.grid.분과위원', '분과위원')),
        width: 160,
        cssClass: 'WijmoFind',
        cellTemplate: (params) => `
          <span>${params.value || ''}</span>
          ${params.value ? '<Button />' : ''}`,
      },
      {
        binding: 'chgCrgeUserNm',
        header: String(t('mp.grid.설비담당자', '설비담당자')),
        width: 100,
        align: 'center',
      },
      {
        header: String(t('mp.label.제작사양서', '제작사양서')),
        columns: [
          {
            binding: 'drwNms',
            header: String(t('mp.label.개정대상', '개정대상')),
            width: 110,
            cssClass: 'WijmoFind',
            cellTemplate: (params) => `
              <span>${params.value ? CommonUtil.unescapeHtml(params.value) : ''}</span> 
              ${params.value ? '<Button />' : ''}`,
          },
          {
            binding: 'spshRevCtnNm',
            header: String(t('mp.label.진행상태', '진행상태')),
            width: 110,
            align: 'center',
          },
        ],
      },
      {
        binding: 'dataInsUserNm',
        header: String(t('com.label.등록자', '등록자')),
        width: 100,
        align: 'center',
      },
      {
        binding: 'dataInsDtm',
        header: String(t('com.label.등록일자', '등록일자')),
        width: 120,
        align: 'center',
      },
      {
        binding: 'dataUpdUserNm',
        header: String(t('com.label.수정자', '수정자')),
        width: 100,
        align: 'center',
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('com.label.수정일자', '수정일자')),
        width: 120,
        align: 'center',
      },
      {
        binding: 'atchFileGrId',
        visible: false,
      },
    ];
  }, [isChangeColumn, flexRef]);

  const onInitialized = (grid) => {
    gridRef.current = grid;
    setflexRef(gridRef);

    grid.hostElement.addEventListener('click', (e) => {
      const len = grid.rows.length;
      if (len == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;
      if (ht.panel.cellType != 1) return;

      const item = grid.rows[ht.row].dataItem;
      // 관심항목 등록 및 삭제 추가
      if (e.target instanceof HTMLButtonElement) {
        const bmkCondition = {
          bmkAtcTpCd: BmkAtcTpCd.MP_LIST,
          bmkTgtTpCd: BmkTgtTpCd.BMK_COND,
          bmkGrDesc: location.pathname,
          itemId: item.itemId, //detail 즐겨찾기키값내용(bmkAtcCd)
        };
        switch (e.target.id) {
          case 'bmkBtn':
            saveCondBookmarks(bmkCondition).then((res) => {
              if (res.successOrNot === 'Y') {
                item.bmkYn = item.bmkYn === 'N' ? 'Y' : 'N';
                gridRef.current.refresh();
              }
              if (res.successOrNot === 'N') {
                openMessageBar({
                  type: 'error',
                  content: t(
                    'com.label.저장 중 오류가 발생했습니다.',
                    '저장 중 오류가 발생했습니다.'
                  ),
                });
              }
            });
            break;
        }
      }

      if (ht.panel === grid.cells) {
        const binding = grid.columns[ht.col].binding;
        // 상태이력
        if ('itemStatCdNm' === binding) {
          setMpItemStatHisModalCondition({
            mpItemId: item.itemId,
            mcrcIds: item.mcrcIds,
          });
          setMpItemStatHisModalOpen(true);
        }
        // 대상설비
        if ('eqpNms' === binding) {
          if (item.eqpNms) {
            setMpItemEqpListModalCondition({
              mpItemId: item.itemId,
            });
            setOpenMpItemEqpListModal(true);
          }
        }
        // 분과위원
        if ('mcrcNms' === binding) {
          if (item.mcrcIds) {
            setMcrcPopupCondition({
              mpClsfType: item.mpClsfCd,
              prdnProcCode: item.procCd,
            });
            setOpenMcrcPopup(true);
          }
        }
        // 일반첨부
        if ('atchFileCnt' === binding && item.atchFileCnt > 0) {
          handleOpenFileDownload(item.atchFileGrId, MpItemAtchFileTpCd.MP_ITEM);
        }
        // 개조/개선 Item 보고서
        if ('mpItemRptAtchFileCnt' === binding && item.mpItemRptAtchFileCnt > 0) {
          handleOpenFileDownload(item.atchFileGrId, MpItemAtchFileTpCd.MP_ITEM_RPT);
        }
        // 유효성 평가보고서
        if ('vaidEvlRptAtchFileCnt' === binding && item.vaidEvlRptAtchFileCnt > 0) {
          handleOpenFileDownload(item.atchFileGrId, MpItemAtchFileTpCd.VAID_EVL_RPT);
        }
        // 회의록(설비변경 심의)
        if ('scmmMintAtchFileCnt' === binding && item.scmmMintAtchFileCnt > 0) {
          handleOpenFileDownload(item.atchFileGrId, MpItemAtchFileTpCd.SCMM_MINT);
        }
        // Fast Track 근거자료
        if ('mpFstrAtchFileCnt' === binding && item.mpFstrAtchFileCnt > 0) {
          handleOpenFileDownload(item.atchFileGrId, MpItemAtchFileTpCd.MP_FSTR);
        }
        // 회의록(수평전개 심의)
        if ('mpHeRptAtchFileCnt' === binding && item.mpHeRptAtchFileCnt > 0) {
          handleOpenFileDownload(item.atchFileGrId, MpItemAtchFileTpCd.MP_HE_RPT);
        }
        // 제작사양서
        if ('drwNms' === binding) {
          if (item.drwNms && item.itemId) {
            setDrwRetrievePopUpCondition({
              mpItemId: item.itemId,
            });
            setOpenDrwRetrievePopUp(true);
          }
        }
      }
    });
  };

  useEffect(() => {
    // mp 권한체크
    if (hasRole('MP_MANAGER') || hasRole('ADM')) {
      setHasAuth(true);
    }
  }, []);

  const handleSearch = async (condition: MpItemCondition) => {
    setCheckedItems([]);
    setMpItemCondition(condition);
    const response = await findMpItems(condition);
    if (response) {
      const rowData = response || [];
      setRowData(rowData);
    }
  };

  const handleOpenFileDownload = (atchFileGrId: string, atchFileTpCd: string) => {
    setFileUploadModalCondition({
      atchFileGrId: atchFileGrId,
      atchFileTpCd: atchFileTpCd,
      optValCtn1: 'TB_ELM_MP_ITEM_M',
      bizName: 'mp',
      downloadOnly: true,
    });
    setOpenFileUploadModal(true);
  };

  const moveToModify = (itemId: string) => {
    navigate(`/mp/item-management`, {
      state: {
        upprMnuUrl: location.pathname,
        mode: ManagementMode.MODIFY,
        itemId: itemId,
        mnuId: `${location.pathname}/${ManagementMode.MODIFY}`,
        mnuNm: t('mp.label.Item 수정', 'Item 수정'),
      },
    });
  };

  const handleDownloadExcel = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(gridRef.current, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
      formatItem: (args) => {
        if (args.panel === gridRef.current!.cells) {
          const cell = args.getFormattedCell();
          if (cell) {
            args.xlsxCell.value = cell?.textContent?.trim();
          }
        }
      },
    });
    book.sheets[0].name = t('mp.label.Item', 'Item');
    book.saveAsync(
      getExcelFileName(t('mp.label.설비변경점_수평전개_Item', '설비변경점_수평전개_Item'))
    );
  };

  const handleCopyItemRow = async (e) => {
    if (!checkedItems.length) {
      openMessageBar({
        type: 'error',
        content: t('com.label.복사할 행을 선택해 주세요.', '복사할 행을 선택해 주세요.'),
      });
      return;
    } else if (checkedItems.length > 1) {
      openMessageBar({
        type: 'error',
        content: t(
          'com.label.복사할 행은 하나만 선택해 주세요.',
          '복사할 행은 하나만 선택해 주세요.'
        ),
      });
      return;
    }

    // 복사 관련 변경사항 생길지 몰라 주석
    // const response = await getAtchFileGrId();
    let msg = '';
    const rows = checkedItems?.map((row) => {
      if (ItemStatCd.Drop !== row.itemStatCd) {
        msg = 'Drop 상태의 Item만 복사 가능합니다.';
      }

      if (row?.atchFileGrId) {
        const uploadRow =
          {
            ...row,
            atchFileGrId: '',
            atchFileCnt: 0,
            minuteAtchFileCnt: 0,
            mpItemRptAtchFileCnt: 0,
            scmmMintAtchFileCnt: 0,
            specSheetAtchFileCnt: 0,
            vaidEvlRptAtchFileCnt: 0,
          } || [];
        return uploadRow;
      }
      return row;
    });

    if (msg) {
      openMessageBar({
        type: 'error',
        content: t(`com.label.${msg}`, `${msg}`),
      });
      return;
    }

    navigate('/mp/item-management/new', {
      state: {
        upprMnuUrl: location.pathname,
        mode: ManagementMode.CREATE,
        mnuId: `${location.pathname}/${ManagementMode.CREATE}`,
        mnuNm: t('mp.label.Item 등록', 'Item 등록'),
        copyTarget: rows[0],
      },
    });
  };

  const handleAddBookmarkItemRow = async (e) => {
    const rows = checkedItems || [];

    if (!rows.length) {
      openMessageBar({
        type: 'error',
        content: t('ip.msg.item이 선택되지 않았습니다.', 'item이 선택되지 않았습니다.'),
      });
      return;
    } else if (!bmkGrId) {
      openMessageBar({
        type: 'error',
        content: t(
          'mp.msg.나의 관심항목이 선택되지 않았습니다.',
          '나의 관심항목이 선택되지 않았습니다.'
        ),
      });
      return;
    }

    addBookmarkItem({
      bmkGrId: bmkGrId,
      mpItems: rows,
    }).then((result) => {
      openMessageBar({
        type: result?.successOrNot === 'Y' ? 'confirm' : 'error',
        content:
          result?.successOrNot === 'Y'
            ? t('com.label.저장되었습니다.', '저장되었습니다.')
            : t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
      });
      handleSearch({ ...mpItemCondition, bmkGrId: bmkGrId });
    });
  };

  const selectBookmarkList = async () => {
    // 관심항목 조회
    const defaultCondition = {} as MpItemCondition;
    const response = await findMpItems(defaultCondition);
    if (response) {
      const rowData = response.filter((item) => item.bmkYn === 'Y') || [];
      setRowData(rowData);
    }
  };

  /**
   * 템플릿 다운로드
   * // 2025.05.22 - 미사용
  const downloadExcel = async (templateType) => {
    openLoading(true);
    const rows = rowData || [];

    const ids = (rows || []).reduce((acc, cur) => [...acc, cur.itemId as string], [] as string[]);
    downloadExcelTemplatesPost('TPL_MP_LIST', '', ids).finally(() => openLoading(false));
  };
  */

  return (
    <>
      <MPCommonSearch
        type={'item'}
        changeBookmark={(bmkGrId) => setBmkGrId(bmkGrId)}
        onHandleSearch={handleSearch}
      />
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('mp.grid.설비변경점/수평전개 Item', '설비변경점/수평전개 Item')}</h3>
          <span className="total">
            {t('com.label.총', '총')} <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          {/* [25.08.28] 복사 기능도 신규와 동일하기때문에 일반사용자도 가능 */}
          <>
            <Button
              css={IconButton.button}
              className="find"
              onClick={selectBookmarkList}
              disableRipple
            >
              {t('com.button.관심항목조회', '관심항목조회')}
            </Button>
            <Button
              css={IconButton.button}
              className="copyRow"
              disableRipple
              onClick={(e) => handleCopyItemRow(e)}
            >
              {t('com.button.복사', '복사')}
            </Button>
            {/*
            <Button
              css={IconButton.button}
              className="bookmark"
              onClick={(e) => handleAddBookmarkItemRow(e)}
              disableRipple
            >
              {t('com.button.관심항목추가', '관심항목추가')}
            </Button>
            */}
          </>
          {/*
          <Button
            css={IconButton.button}
            className="user"
            onClick={() => {
              navigate('/mp/target');
            }}
            disableRipple
          >
            {t('mp.button.MP 대상/담당자', 'MP 대상/담당자')}
          </Button>
          <Button
            css={IconButton.button}
            className="mail"
            onClick={() => setOpenEmailModal(true)}
            disableRipple
          >
            {t('com.button.자동메일설정', '자동메일설정')}
          </Button>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={() => downloadExcel('TPL_MP_LIST')}
            disableRipple
          >
            {t('com.button.템플릿 다운로드', '템플릿 다운로드')}
          </Button>
          */}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch(mpItemCondition)}
            disableRipple
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
          <GridSettingButtons
            gridId={'mpItemPageGrid'} // <CustomGrid>에 선언한 id와 동일한 값
            layoutDefinition={layoutDefinition} // 그리드 레이아웃
            changeShownColumns={(result) => {
              // 노출필드 변경이벤트
              // ref를 이용해 <CustomGrid> 내 함수를 실행
              //openLoading(true);
              gridRef2.current?.changeColumn?.(result);
              setIsChangeColumn(true);
              gridRef.current.refresh();
              //openLoading(false);
            }}
          />
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        id={'mpItemPageGrid'}
        ref={gridRef2}
        layoutDefinition={layoutDefinition}
        excludeFilter={['bmkYn']}
        excludePin={['bmkYn']}
        rowData={rowData}
        height={525}
        isReadOnly={true}
        initialized={onInitialized}
        onChangeCheckedItem={(items) => setCheckedItems(items)}
      />
      <GlobalBtnGroup>
        {/*
        {hasAuth && (
          <Button
            css={IconButton.button}
            className="write"
            disableRipple
            onClick={(e) => {
              navigate('/mp/excel-management', {
                replace: true,
                state: {
                  upprMnuUrl: location.pathname,
                  mnuId: location.pathname,
                  mnuNm: `MP List Upload`,
                  mode: ManagementMode.CREATE_EXCEL,
                },
              });
            }}
          >
            {t('com.button.엑셀', '엑셀 신규')}
          </Button>
        )}
        */}
        {/*{hasAuth && (*/}
        {/*<>*/}
        {/*outline*/}
        <Button
          css={IconButton.button}
          className="create primaryBtn"
          onClick={() => {
            navigate('/mp/item-management/new', {
              state: {
                upprMnuUrl: location.pathname,
                mode: ManagementMode.CREATE,
                mnuId: `${location.pathname}/${ManagementMode.CREATE}`,
                mnuNm: t('mp.label.Item 등록', 'Item 등록'),
              },
            });
          }}
          disableRipple
        >
          {t('com.button.신규', '신규')}
        </Button>
        {/*<Button*/}
        {/*  css={IconButton.button}*/}
        {/*  className="outline write"*/}
        {/*  onClick={() => {*/}
        {/*    if (checkedItems && checkedItems.length > 0) {*/}
        {/*      if (checkedItems.length > 1) {*/}
        {/*        openMessageBar({*/}
        {/*          type: 'error',*/}
        {/*          content: t(*/}
        {/*            'mp.msg.수정할 대상을 1건만 선택해 주세요.',*/}
        {/*            '수정할 대상을 1건만 선택해 주세요.'*/}
        {/*          ),*/}
        {/*        });*/}
        {/*        return;*/}
        {/*      }*/}
        {/*      if (checkedItems[0].itemId) {*/}
        {/*        moveToModify(checkedItems[0].itemId);*/}
        {/*      }*/}
        {/*    } else {*/}
        {/*      openMessageBar({*/}
        {/*        type: 'error',*/}
        {/*        content: t('mp.msg.수정할 대상을 선택해 주세요.', '수정할 대상을 선택해 주세요.'),*/}
        {/*      });*/}
        {/*    }*/}
        {/*  }}*/}
        {/*  disableRipple*/}
        {/*>*/}
        {/*  {t('com.button.수정', '수정')}*/}
        {/*</Button>*/}
        {/*</>*/}
        {/*)}*/}
      </GlobalBtnGroup>

      {isOpenFileUploadModal && (
        <FileUploadPopUp
          open={isOpenFileUploadModal}
          close={() => setOpenFileUploadModal(false)}
          initParam={{
            atchFileGrId: fileUploadModalCondition.atchFileGrId,
            atchFileTpCd: fileUploadModalCondition.atchFileTpCd,
            optValCtn1: fileUploadModalCondition.tableName,
            bizName: fileUploadModalCondition.bizName,
          }}
          downloadOnly={true}
          onCallback={(atchFileGrId, fileCount) => {
            if (fileUploadModalCondition.target) {
              fileUploadModalCondition.target.node?.setDataValue(
                fileUploadModalCondition.target.column?.getColId(),
                fileCount
              );
            }
            setOpenFileUploadModal(false);
          }}
        />
      )}
      {isOpenEmailModal && (
        <AutoSendEmailModal
          open={isOpenEmailModal}
          close={() => setOpenEmailModal(false)}
          condition={{
            bizName: BizName.MP, // 이메일업무구분코드
            bmkAtcTpCd: BmkAtcTpCd.MP_LIST, //즐겨찾기항목유형코드
          }}
        />
      )}

      {/*변경점 상태 이력*/}
      {isMpItemStatHisModalOpen && mpItemStatHisModalCondition?.mpItemId && (
        <MpItemStatHisModal
          open={isMpItemStatHisModalOpen}
          close={() => {
            setMpItemStatHisModalCondition({
              mpItemId: '',
              mcrcIds: '',
            });
            setMpItemStatHisModalOpen(false);
          }}
          mpItemId={mpItemStatHisModalCondition?.mpItemId} // MP품목ID
          mcrcIds={mpItemStatHisModalCondition?.mcrcIds || ''} // MP품목ID
        />
      )}
      {/* 대상 설비 목록 (readonly) */}
      {isOpenMpItemEqpListModal && mpItemEqpListModalCondition?.mpItemId && (
        <MpEquipmentPopUp
          open={isOpenMpItemEqpListModal}
          close={() => setOpenMpItemEqpListModal(false)}
          condition={{ mpItemId: mpItemEqpListModalCondition?.mpItemId || '', horExplYn: 'N' }}
          title={t('mp.label.대상설비목록', '대상설비목록')}
        />
      )}

      {isOpenMcrcPopup && mcrcPopupCondition?.mpClsfType && mcrcPopupCondition?.prdnProcCode && (
        <MpMcrcPopUp
          open={isOpenMcrcPopup}
          close={() => setOpenMcrcPopup(false)}
          condition={{
            mpClsfType: mcrcPopupCondition?.mpClsfType,
            prdnProcCode: mcrcPopupCondition?.prdnProcCode,
          }}
        />
      )}

      {isOpenDrwRetrievePopUp && (
        <MpDrwRetrievePopUp
          open={isOpenDrwRetrievePopUp}
          close={() => setOpenDrwRetrievePopUp(false)}
          condition={{
            mpItemId: drwRetrievePopUpCondition?.mpItemId,
            // drwId: drwRetrievePopUpCondition?.drwId, // [25.07.30] 제작사양서 요청 시 1:N
          }}
          onCallbackData={null}
        />
      )}
    </>
  );
};

export default MpItemPage;
